import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
    Button,
    Card,
    Center,
    Loader,
    PasswordInput,
    Stack,
    Text,
    TextInput,
    Title,
} from '@mantine/core';
import { clearMerchantIdCache } from '@api';
import type { LoginPayloadResponse } from '@api/consultantInvite';

/** The two facts any accept page needs about its invitation. */
export type InviteView = {
    email: string;
    firstName?: string | null;
    lastName?: string | null;
    /** Consultant invites report acceptance this way; vendor lookups 404 instead. */
    isUsed?: boolean;
};

export interface AcceptInvitePageProps {
    /** "You've been invited to access Reparo as a consultant." - the one line that differs. */
    description: string;
    fetchInvite: (token: string) => Promise<InviteView>;
    accept: (args: {
        token: string;
        password: string;
        firstName?: string;
        lastName?: string;
    }) => Promise<LoginPayloadResponse>;
    /** Where the new session lands - '/' for a consultant, '/vendor/home' for a site login. */
    landing: string;
}

/**
 * Set-up-your-account, for anybody holding an invitation link.
 *
 * ONE PAGE, TWO INVITE TYPES. This was AcceptConsultantInvite until vendor invites arrived
 * with the identical shape - fetch the invite, choose a password, accept, land signed in.
 * Only four things differ (the API pair, one line of copy, the landing route), so they are
 * props and everything else is shared rather than copied into a twin that drifts.
 *
 * THE HARD RELOAD AT THE END IS LOAD-BEARING. The acceptor may have been signed in as somebody
 * else moments ago; storage is cleared, the new session written, and window.location forces a
 * full reload so no React state from the previous user survives into the new one.
 */
const AcceptInvitePage: React.FC<AcceptInvitePageProps> = ({
    description,
    fetchInvite,
    accept,
    landing,
}) => {
    const { token } = useParams<{ token: string }>();
    const navigate = useNavigate();

    const [invite, setInvite] = useState<InviteView | null>(null);
    const [loadingInvite, setLoadingInvite] = useState(true);
    const [inviteError, setInviteError] = useState<string | null>(null);

    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);

    useEffect(() => {
        if (!token) return;
        const load = async () => {
            setLoadingInvite(true);
            try {
                const data = await fetchInvite(token);
                if (data.isUsed) {
                    navigate('/login', {
                        state: { message: 'Your account is already set up. Please log in.' },
                        replace: true,
                    });
                    return;
                }
                setInvite(data);
                setFirstName(data.firstName ?? '');
                setLastName(data.lastName ?? '');
            } catch {
                setInviteError('This invite link is invalid or has expired.');
            } finally {
                setLoadingInvite(false);
            }
        };
        void load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitError(null);

        if (password.length < 6) {
            setSubmitError('The password you entered is not supported. Password must be at least 6 characters.');
            return;
        }
        if (!/^[\d!#$%&*@A-Z^a-z]*$/.test(password)) {
            setSubmitError('The password you entered is not supported. Allowed characters: A–Z, a–z, 0–9, ! # $ % & * @ ^');
            return;
        }
        if (password !== confirmPassword) {
            setSubmitError('Passwords do not match.');
            return;
        }
        if (!token) return;

        setSubmitting(true);
        try {
            const result = await accept({
                token,
                password,
                firstName: firstName.trim() || undefined,
                lastName: lastName.trim() || undefined,
            });

            // Clear ALL previous session data first
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            clearMerchantIdCache();

            // Store the new credentials
            localStorage.setItem('token', result.accessToken.token);
            localStorage.setItem('user', JSON.stringify(result.user));

            // Force a full page reload to ensure clean state - no stale React state from a
            // previous user survives it.
            window.location.href = landing;
        } catch {
            setSubmitError('Failed to set up your account. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    if (loadingInvite) {
        return (
            <Center h="100vh">
                <Loader />
            </Center>
        );
    }

    if (inviteError) {
        return (
            <Center h="100vh">
                <Card withBorder radius="md" p="xl" w={400}>
                    <Stack align="center" gap="sm">
                        <Title order={3}>Invalid Invite</Title>
                        <Text c="dimmed" ta="center">
                            {inviteError}
                        </Text>
                    </Stack>
                </Card>
            </Center>
        );
    }

    return (
        <Center h="100vh">
            <Card withBorder radius="md" p="xl" w={420}>
                <form onSubmit={handleSubmit}>
                    <Stack gap="md">
                        <Stack gap={4}>
                            <Title order={3}>Set Up Your Account</Title>
                            <Text size="sm" c="dimmed">
                                {description}
                            </Text>
                        </Stack>

                        <TextInput label="Email" value={invite?.email ?? ''} readOnly />

                        <TextInput
                            label="First Name"
                            placeholder="First name"
                            value={firstName}
                            onChange={(e) => setFirstName(e.currentTarget.value)}
                        />

                        <TextInput
                            label="Last Name"
                            placeholder="Last name"
                            value={lastName}
                            onChange={(e) => setLastName(e.currentTarget.value)}
                        />

                        <PasswordInput
                            label="Password"
                            placeholder="At least 6 characters"
                            value={password}
                            onChange={(e) => setPassword(e.currentTarget.value)}
                            required
                        />

                        <PasswordInput
                            label="Confirm Password"
                            placeholder="Repeat password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.currentTarget.value)}
                            required
                        />

                        {submitError && (
                            <Text size="sm" c="red">
                                {submitError}
                            </Text>
                        )}

                        <Button type="submit" loading={submitting} fullWidth>
                            Create Account
                        </Button>
                    </Stack>
                </form>
            </Card>
        </Center>
    );
};

export default AcceptInvitePage;
